import React from 'react'
import { Box, Tooltip, Typography } from '@mui/material'
import useCustomTranslation from 'src/@core/hooks/useCustomTranslation'

const GetBadgeLabel = ({ label, size = 'small', showTooltip = false }) => {
  const translation = useCustomTranslation()
  if (!label) return null

  const badgeConfig = {
    NEW: { backgroundColor: '#F2F0EF', color: '#000000' },
    OS1: { backgroundColor: '#BDBDBD', color: '#000000' },
    OS2: { backgroundColor: '#1E88E5', color: '#FFFFFF' },
    OS3: { backgroundColor: '#43A047', color: '#FFFFFF' },
    OS4: { backgroundColor: '#8E24AA', color: '#FFFFFF' },
    OS5: { backgroundColor: '#FBC02D', color: '#000000' }
  }

  const config = badgeConfig[label] || { backgroundColor: '#8E8E8E', color: '#FFFFFF' }

  const description = translation.translate(`BadgeLevels.${label}.description`)

  const isLarge = size === 'large'

  const BadgeContent = (
    <Box
      component='span'
      sx={{
        backgroundColor: config.backgroundColor,
        color: config.color,
        padding: isLarge
          ? {
              xs: '3px 14px 2px 8px',
              md: '4px 20px 6px 10px'
            }
          : '3px 14px 2px 8px',
        fontWeight: 700,
        fontSize: isLarge ? '15px' : '11px',
        clipPath: 'polygon(0 0, 85% 0, 100% 50%, 85% 100%, 0 100%)',
        minWidth: isLarge ? '75px' : '50px',
        textAlign: 'center',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        lineHeight: 1.5,
        textTransform: 'uppercase',
        borderTopLeftRadius: '2px',
        borderBottomLeftRadius: '2px',
        flexShrink: 0,
        verticalAlign: 'middle',
        ml: 1,
        cursor: showTooltip ? 'pointer' : 'default'
      }}
    >
      {label}
    </Box>
  )

  const levels = ['NEW', 'OS1', 'OS2', 'OS3', 'OS4', 'OS5']
  const activeIndex = levels.indexOf(label)

  if (showTooltip && description) {
    return (
      <Tooltip
        title={
          <Box sx={{ p: 0.5, textAlign: 'center' }}>
            <Typography
              sx={{
                fontWeight: 500,
                color: 'inherit',
                display: 'block',
                lineHeight: 1.2,
                fontSize: { xs: '11px', sm: '12.5px' },
                mb: 1
              }}
            >
              {description}
            </Typography>
            <Box sx={{ display: 'flex', gap: '4px', justifyContent: 'center', alignItems: 'center' }}>
              {levels.map((lvl, index) => (
                <Typography
                  key={lvl}
                  sx={{
                    fontSize: '14px',
                    lineHeight: 1,
                    color:
                      index <= activeIndex
                        ? config.color
                        : config.color === '#FFFFFF'
                          ? 'rgba(255,255,255,0.25)'
                          : 'rgba(0,0,0,0.15)',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  ▲
                </Typography>
              ))}
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 700,
                  color: config.color,
                  ml: '6px',
                  mt: 1,
                  lineHeight: 1
                }}
              >
                Level: {activeIndex + 1}/{levels.length}
              </Typography>
            </Box>
          </Box>
        }
        componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: config.backgroundColor,
              color: config.color,
              boxShadow: '0px 2px 4px rgba(0,0,0,0.2)',
              '& .MuiTooltip-arrow': {
                color: config.backgroundColor
              }
            }
          }
        }}
        arrow
        placement='top'
      >
        {BadgeContent}
      </Tooltip>
    )
  }

  return BadgeContent
}

GetBadgeLabel.defaultProps = {
  showTooltip: false
}

export default GetBadgeLabel
